import { faTimes } from '@fortawesome/pro-regular-svg-icons'
import { ChangeEvent, ReactNode, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from 'react-query'
import { useNavigate, useParams } from 'react-router'
import IWButton from 'shared/components/thunderbolt/IWButton'
import IWGenericCard from 'shared/components/thunderbolt/IWGenericCard'
import IWLoading from 'shared/components/thunderbolt/IWLoading'
import IWTextArea from 'shared/components/thunderbolt/IWTextArea'
import IWTextInput, {
  IWFileInput,
} from 'shared/components/thunderbolt/IWTextInput'
import { useToast } from 'shared/components/thunderbolt/IWToastContext'
import IWToggle from 'shared/components/thunderbolt/IWToggle'
import IWTypography from 'shared/components/thunderbolt/IWTypography'
import {
  getLsAdminCredentialById,
  OrgCredential,
  updateOrgCredential,
} from 'shared/loadSchedulingClient'
import styled from 'styled-components'

export type UpdateOrgCredential = Pick<
  OrgCredential,
  | 'credentialId'
  | 'userId'
  | 'userAccount'
  | 'userSubAccount'
  | 'pfx'
  | 'isSuma'
  | 'isActive'
> & {
  userPassword?: string
  pfxPassphrase?: string
}

const Wrapper = styled.div`
  display: flex;
  justify-content: left;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: 20rem;
`

const StyledSectionRow = styled.div`
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
  justify-content: left;
  flex-direction: column;
  margin: 0 auto 0 0;

  & > * {
    flex: 0 0 calc(25% - 0.75rem); /* 25% width for 4 items, accounting for gap */
  }
`

const StyledFooter = styled.footer`
  display: flex;
  align-items: center;
  justify-content: left;
  gap: 0.5rem;
`

const PreferencesSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`

const CardContent = styled.div`
  display: flex;
  padding: 1rem;
  flex-direction: column;
  gap: 1rem;
`

const StyledInfoBlock = styled.div`
  display: flex;
  flex-grow: 1;
  flex-direction: column;
  gap: 0.5rem;
`

const StyledInfoSection = styled.div`
  display: flex;
  flex-direction: row;
  gap: 1rem;
`

const StyledIWTextAreaWrapper = styled.div`
  margin-top: 1rem;
`

const Section = ({
  title,
  children,
}: {
  title?: string
  children: ReactNode
}) => {
  return (
    <PreferencesSection>
      {title && (
        <IWTypography size="lg" weight="medium">
          {title}
        </IWTypography>
      )}
      <IWGenericCard>
        <CardContent>
          <StyledInfoSection>
            <StyledInfoBlock>{children}</StyledInfoBlock>
          </StyledInfoSection>
        </CardContent>
      </IWGenericCard>
    </PreferencesSection>
  )
}

export default function LsAdminEditCredential() {
  const { t } = useTranslation()
  const { credentialId = '' } = useParams()
  const navigate = useNavigate()
  const toast = useToast()
  const filesRef = useRef<HTMLInputElement | null>(null)
  const [market, setMarket] = useState('')
  const [brand, setBrand] = useState('')
  const [zone, setZone] = useState('')
  const [location, setLocation] = useState('')
  const [locationName, setLocationName] = useState('')
  const [userId, setUserId] = useState('')
  const [userAccount, setUserAccount] = useState('')
  const [userSubAccount, setUserSubAccount] = useState('')
  const [userPassword, setUserPassword] = useState('')
  const [pfx, setPFX] = useState('')
  const [pfxPassphrase, setPFXPassphrase] = useState('')
  const [isSuma, setIsSuma] = useState(false)
  const [isActive, setIsActive] = useState(false)
  const [, setPfxFile] = useState<File>()
  const [isUpdating, setIsUpdating] = useState(false)

  const { data: credential, isLoading } = useQuery({
    queryKey: [credentialId],
    queryFn: async () => {
      return getLsAdminCredentialById(credentialId)
    },
  })

  const handleNavigateBack = () => {
    navigate('/admin/load-scheduling/credentials')
  }

  const hasErrors = () => {
    if (!zone || !zone.length) {
      return true
    }

    if (!location || !location.length) {
      return true
    }

    if (!locationName || !locationName.length) {
      return true
    }

    return false
  }

  const handleSave = async () => {
    if (hasErrors()) {
      return
    }

    setIsUpdating(true)

    try {
      const updatedCredential = {
        credentialId,
        zone,
        location,
        locationName,
        userId,
        userAccount,
        userPassword: undefined,
        userSubAccount,
        pfx,
        pfxPassphrase: undefined,
        isSuma,
        isActive,
      } as UpdateOrgCredential

      if (userPassword && userPassword !== credential?.userPassword) {
        updatedCredential.userPassword = userPassword
      }

      if (pfxPassphrase && pfxPassphrase !== credential?.pfxPassphrase) {
        updatedCredential.pfxPassphrase = pfxPassphrase
      }

      await updateOrgCredential(updatedCredential)

      setIsUpdating(false)

      handleNavigateBack()
    } catch (error) {
      toast.alert(error?.response?.data, {
        canDismiss: true,
        duration: 10_000,
      })
    } finally {
      setIsUpdating(false)
    }
  }

  const clearInputFiles = (): void => {
    if (filesRef?.current) {
      filesRef.current.value = ''
    }
    setPfxFile(undefined)
    setPFX('')
  }

  const onFileSelect = (event: ChangeEvent<HTMLInputElement>): void => {
    const files: File[] = event && Array.from(event.target.files || [])
    const hasFiles = Boolean(files?.length > 0)

    if (!hasFiles) {
      clearInputFiles()
    }

    const [file] = files
    setPfxFile(file)

    const reader = new FileReader()
    reader.onload = () => {
      const base64String = reader.result?.toString().split(',')[1] || ''
      setPFX(base64String)
    }
    reader.readAsDataURL(file)
  }

  useEffect(() => {
    if (credential) {
      setMarket(credential?.market)
      setBrand(credential?.brand)
      setZone(credential?.zone)
      setLocation(credential?.location)
      setLocationName(credential?.locationName)
      setIsSuma(credential?.isSuma)
      setIsActive(credential?.isActive)
      setUserId(credential?.userId)
      setUserAccount(credential?.userAccount ?? '')
      setUserPassword(credential?.userPassword ?? '')
      setUserSubAccount(credential?.userSubAccount)
      setPFX(credential?.pfx)
      setPFXPassphrase(credential?.pfxPassphrase)
    }
  }, [credential])

  return (
    <main>
      <Wrapper>
        {isLoading && <IWLoading />}

        <Section title={t('loadScheduling.labels.configuration')}>
          <StyledSectionRow>
            <IWTextInput
              label={t('loadScheduling.labels.market')}
              required
              disabled
              value={market}
            />

            <IWTextInput
              label={t('loadScheduling.labels.brand')}
              required
              disabled
              value={brand}
            />
          </StyledSectionRow>
        </Section>

        <Section title={t('loadScheduling.labels.pnodeInfo')}>
          <StyledSectionRow>
            <IWTextInput
              label={t('loadScheduling.labels.zone')}
              required
              disabled
              hasError={!zone || !zone.length}
              value={zone}
            />

            <IWTextInput
              label={t('loadScheduling.labels.locationName')}
              required
              disabled
              hasError={!locationName || !locationName.length}
              value={locationName}
            />

            <IWTextInput
              label={t('loadScheduling.labels.location')}
              required
              disabled
              hasError={!location || !location.length}
              value={location}
            />
          </StyledSectionRow>
        </Section>

        <Section title={t('loadScheduling.labels.credentials')}>
          <StyledSectionRow>
            <IWTextInput
              label={t('loadScheduling.labels.userId')}
              value={userId}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                setUserId(e.target.value)
              }
            />

            <IWTextInput
              label={t('loadScheduling.labels.userAccount')}
              value={userAccount}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                setUserAccount(e.target.value)
              }
            />

            <IWTextInput
              label={t('loadScheduling.labels.userSubAccount')}
              value={userSubAccount}
              placeholder="Default"
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                setUserSubAccount(e.target.value)
              }
            />

            <IWTextInput
              label={t('loadScheduling.labels.userPassword')}
              value={userPassword}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                setUserPassword(e.target.value)
              }
            />
          </StyledSectionRow>
        </Section>

        <Section title={t('loadScheduling.labels.certificate')}>
          <StyledSectionRow>
            <IWFileInput
              ref={filesRef}
              label={t('loadScheduling.labels.uploadPFX')}
              type="file"
              multiple={false}
              name="file"
              accept=".pfx"
              trailingIcon={{
                icon: faTimes,
                onIconClick: () => clearInputFiles(),
              }}
              onChange={onFileSelect}
            />

            <IWTextInput
              label={t('loadScheduling.labels.PFXPassphrase')}
              value={pfxPassphrase}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                setPFXPassphrase(e.target.value)
              }
            />
          </StyledSectionRow>

          <StyledIWTextAreaWrapper>
            <IWTextArea
              disabled
              label={t('loadScheduling.labels.PFX')}
              value={pfx}
              rows={20}
              onChange={(e: ChangeEvent<HTMLTextAreaElement>) =>
                setPFX(e.target.value)
              }
            />
          </StyledIWTextAreaWrapper>
        </Section>

        <Section title={t('loadScheduling.labels.settings')}>
          <IWToggle
            id="ls-new-cred-is-suma"
            label={t('loadScheduling.labels.isSUMA')}
            checked={isSuma}
            onChange={() => {
              setIsSuma((p) => !p)
            }}
          />

          <IWToggle
            id="ls-new-cred-is-active"
            label={t('loadScheduling.labels.isActive')}
            checked={isActive}
            onChange={() => {
              setIsActive((p) => !p)
            }}
          />
        </Section>

        <StyledFooter>
          <IWButton color="grey" variant="outline" onClick={handleNavigateBack}>
            {t('button.cancel')}
          </IWButton>

          <IWButton onClick={handleSave} disabled={hasErrors() || isUpdating}>
            {t('button.save')}
          </IWButton>
        </StyledFooter>
      </Wrapper>
    </main>
  )
}
